import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CloseOutlined, SearchOutlined } from '@fx-ui/fine-design/icons';
import classNames from 'classnames';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { useDebouncedValue } from '~/hooks';
import { EMPTY_FN, KEY_CODE } from '~/constants';
import {
    assign,
    escapeRegexp,
    forEach,
    get,
    isEmpty,
    isFunction,
    isNumber,
    map,
    post,
    size as lodashSize,
    trim
} from '~/utils';
import Intl from '@fx-ui/intl';
import { HighLightText } from '~/components';

type ISize = 'normal' | 'small';

interface IAsync {
    url: string;
    data: any;
    config?: any;
}

interface ISearchListProps {
    className?: string;
    value: string;
    async?: IAsync;
    items?: any[];
    keyword?: string;
    keyName?: string;
    maxShow?: number;
    maxVisible?: number;
    isHighLight?: boolean;
    showSearchResult?: boolean;
    keyMatchOnly?: boolean;
    normalInputStyle?: boolean;
    size?: ISize;
    placeholder?: string;
    onFocus?: (event: React.FocusEvent) => void;
    onBlur?: (event: React.FocusEvent) => void;
    onPopupVisibleChange?: (visible: boolean) => void;
    onReturn?: (item: any) => void;
    onInputChange?: (val: string) => void; // 防抖后的输入响应
    onInputInstantChange?: (val: string) => void; // 输入立即响应
    onEmptyInput?: () => void;
    onClose?: () => void;
    onItemIconRender?: (item: any) => React.ReactNode;
    onItemRender?: (item: any, keyword: string) => React.ReactNode;
    onEmptyRender?: () => React.ReactNode;
    onLoadingRender?: () => React.ReactNode;
    onItemClick?: (item: any) => void;
    onAsyncSuccess?: (res: any) => void;
    onAsyncError?: (res: any) => void;
    onSearch?: (val: string) => void;
    maxLength?: number;
    errMsg?: string;
    debounceTime?: number; // 延迟搜索触发时间，ms
    autoSelect?: boolean;
}

const DEFAULT_LOADING_RENDER = () => {
    return <li className="empty-content">{Intl.get('searching')}</li>;
};

const DEFAULT_EMPTY_RENDER = () => {
    return <li className="empty-content">{Intl.get('template.not.found')}</li>;
};

const SearchList = React.forwardRef((props: ISearchListProps, ref) => {
    const {
        className,
        value,
        async,
        items = [],
        keyword = 'keyword',
        keyName = 'name',
        keyMatchOnly = true, // 仅展示与关键字匹配的内容
        maxShow = 30,
        maxVisible = 8,
        isHighLight = true,
        showSearchResult = true,
        normalInputStyle = false,
        size = 'normal',
        placeholder = Intl.get('form.search.placeholder'),
        onBlur = EMPTY_FN,
        onFocus = EMPTY_FN,
        onPopupVisibleChange = EMPTY_FN,
        onReturn,
        onInputInstantChange = EMPTY_FN,
        onInputChange,
        onEmptyInput,
        onClose,
        onItemIconRender,
        onItemRender,
        onEmptyRender = DEFAULT_EMPTY_RENDER,
        onLoadingRender = DEFAULT_LOADING_RENDER,
        onItemClick,
        onAsyncSuccess,
        onAsyncError,
        onSearch,
        maxLength,
        errMsg,
        autoSelect,
        debounceTime = 500
    } = props;

    const [val, setVal] = useState('');
    const [debouncedVal, setDebouncedVal] = useState('');
    const [showPopover, debouncedShowPopover, setShowPopover] = useDebouncedValue(false, 500);
    const [searching, setSearching] = useState<boolean>(false);
    const [currentIdx, setCurrentIdx] = useState<number>(0);

    const searchRef = useRef<any>();
    const inputRef = useRef<any>();
    const listRef = useRef<any>();
    const thisRef = useRef<any>({
        isFocus: false,
        timer: null
    });
    const compositionRef = useRef<boolean>(false);
    const searchTimerRef = useRef<NodeJS.Timeout>();

    const focus = () => {
        inputRef.current.focus();
    };

    const closePopover = () => {
        onPopupVisibleChange(false);
        setShowPopover(false);
    };

    useImperativeHandle(ref, () => {
        return {
            focus,
            closePopover
        };
    });

    useEffect(() => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (value !== val.trim()) {
            setVal(value);
        }
    }, [value]);

    const handleChange = (v: string) => {
        setVal(v);
        if (!compositionRef.current) {
            const value = v.trim();
            setShowPopover(!isEmpty(value));
            onPopupVisibleChange(!isEmpty(value));
            onInputInstantChange?.(value);
            const searchTimer = searchTimerRef.current;
            if (searchTimer) {
                clearTimeout(searchTimer);
            }
            searchTimerRef.current = setTimeout(() => {
                // 如果搜索前后关键字没有发生变化，则不需要触发重新搜索
                if (v === debouncedVal) {
                    return;
                }
                setDebouncedVal(v);
                setCurrentIdx(0);
                if (listRef.current) {
                    listRef.current.scrollTop = 0;
                }
                const key = v.trim();
                if (key) {
                    if (showSearchResult) {
                        if (async) {
                            setSearching(true);
                            getAsyncItems(key);
                        }
                    }
                    onSearch && onSearch(key);
                } else {
                    onEmptyInput && onEmptyInput();
                }
                onInputChange?.(key);
            }, debounceTime);
        }
    };

    const getAsyncItems = (key: string) => {
        const { url, data, config } = async!;
        assign(data, { [keyword]: key });
        post(url, data, config)
            .then((res) => {
                onAsyncSuccess && onAsyncSuccess(res);
            })
            .catch((res) => {
                onAsyncError && onAsyncError(res);
            })
            .finally(() => {
                setSearching(false);
            });
    };

    /**
     * 根据关键词过滤items
     *
     * @param dealItem 是否需要处理item
     */
    const getFilterItems = (dealItem?: boolean) => {
        const filter: any[] = [];
        const key = escapeRegexp(trim(debouncedVal));
        if (!key) {
            return filter;
        }
        forEach(items, (item) => {
            const originItem = assign({}, item);
            const name = get(item, keyName);
            if (name && new RegExp(key, 'i').test(name)) {
                if (dealItem) {
                    const temp = assign({ originItem }, item, {
                        fx_filterName: [name]
                    });
                    if (isHighLight) {
                        const hightLIghtText = (
                            <HighLightText text={name} keyword={key} abbreviated={false} />
                        );
                        assign(temp, { fx_filterName: [hightLIghtText] });
                    }
                    const itemIcon = onItemIconRender && onItemIconRender(item);
                    if (itemIcon) {
                        assign(temp, { fx_icon: itemIcon });
                    }
                    filter.push(temp);
                } else {
                    filter.push(assign({ originItem }, item));
                }
                if (filter.length >= maxShow) {
                    return false;
                }
            } else {
                if (!keyMatchOnly) {
                    if (dealItem) {
                        const temp = assign({ originItem }, item, {
                            fx_filterName: [name]
                        });
                        const itemIcon = onItemIconRender && onItemIconRender(item);
                        if (itemIcon) {
                            assign(temp, { fx_icon: itemIcon });
                        }
                        filter.push(temp);
                    } else {
                        filter.push(assign({ originItem }, item));
                    }
                    if (filter.length >= maxShow) {
                        return false;
                    }
                }
            }
        });
        return filter;
    };

    const handleClose = () => {
        if (thisRef.current.timer) {
            clearTimeout(thisRef.current.timer);
            if (thisRef.current.isFocus) {
                focus();
            }
        }
        handleChange('');
        closePopover();
        onClose && onClose();
    };

    const handleKeydown = (event: React.KeyboardEvent) => {
        switch (event.keyCode) {
            case KEY_CODE.ENTER: {
                const currentItem = getCurrentItem();
                if (currentItem) {
                    closePopover();
                    onReturn && onReturn(currentItem.originItem);
                    onInputChange && onInputChange(get(currentItem, keyName));
                }
                break;
            }
            case KEY_CODE.DOWN:
                dealDownKeydown();
                break;
            case KEY_CODE.UP:
                dealUpKeydown();
                break;
            default:
                break;
        }
    };

    const dealDownKeydown = () => {
        if (listRef.current) {
            const firstItem = listRef.current.querySelector('.filter-item');
            if (firstItem) {
                const itemHeight = firstItem.clientHeight;
                const filterItems = getFilterItems();
                const maxLength = filterItems.length;
                if (currentIdx === maxLength - 1) {
                    setCurrentIdx(0);
                    listRef.current.scrollTop = 0;
                } else {
                    setCurrentIdx((idx) => idx + 1);
                    if (currentIdx + 1 === maxLength - 1) {
                        listRef.current.scrollTop = (maxLength - 1) * itemHeight;
                    } else {
                        const minScrollTop = (currentIdx + 1 - (maxVisible - 1)) * itemHeight;
                        if (minScrollTop > listRef.current.scrollTop) {
                            listRef.current.scrollTop = minScrollTop;
                        }
                    }
                }
            }
        }
    };

    const dealUpKeydown = () => {
        if (listRef.current) {
            const firstItem = listRef.current.querySelector('.filter-item');
            if (firstItem) {
                const itemHeight = firstItem.clientHeight;
                if (currentIdx === 0) {
                    const filterItems = getFilterItems();
                    const maxLength = filterItems.length;
                    setCurrentIdx(maxLength - 1);
                    listRef.current.scrollTop = (maxLength - 1) * itemHeight;
                } else {
                    setCurrentIdx((idx) => idx - 1);
                    const maxScrollTop = (currentIdx - 1) * itemHeight;
                    if (maxScrollTop < listRef.current.scrollTop) {
                        listRef.current.scrollTop = maxScrollTop;
                    }
                }
            }
        }
    };

    const getCurrentItem = () => {
        const filterItems = getFilterItems();
        if (isEmpty(filterItems)) {
            return null;
        }
        return filterItems![currentIdx];
    };

    const renderContent = () => {
        const filterItems = getFilterItems(true);
        return (
            <ul className="item-list" ref={listRef}>
                {searching || debouncedVal !== val ? (
                    onLoadingRender()
                ) : (
                    <>
                        {map(filterItems, (item, index) => {
                            return isFunction(onItemRender) ? (
                                onItemRender(item.originItem, debouncedVal.trim())
                            ) : (
                                <li
                                    className={classNames('filter-item', {
                                        select: index === currentIdx
                                    })}
                                    title={get(item, keyName)}
                                    onClick={() => {
                                        closePopover();
                                        onInputChange && onInputChange(get(item, keyName));
                                        onItemClick && onItemClick(item.originItem);
                                    }}
                                    onMouseEnter={() => setCurrentIdx(index)}
                                    key={index}
                                >
                                    {item.fx_icon}
                                    <div className="filter-name">{item.fx_filterName}</div>
                                </li>
                            );
                        })}
                        {filterItems.length >= maxShow && (
                            <li className="empty-content">
                                {Intl.get('search.max.show.tip', { count: maxShow })}
                            </li>
                        )}
                        {isEmpty(filterItems) && onEmptyRender()}
                    </>
                )}
            </ul>
        );
    };

    return (
        <div className={classNames('fx-search-list', 'size-' + size, className)} ref={searchRef}>
            <ErrorInput
                style={{ width: '100%' }}
                errMsg={errMsg}
                inputState={
                    isNumber(maxLength) && lodashSize(trim(val)) > maxLength ? 'error' : undefined
                }
                ref={inputRef}
                value={val}
                onCompositionStart={() => {
                    compositionRef.current = true;
                }}
                onCompositionEnd={(c) => {
                    const target = c.target as HTMLInputElement;
                    compositionRef.current = false;
                    handleChange(target.value);
                }}
                onChange={handleChange}
                className={classNames(normalInputStyle ? 'normal-style-input' : 'search-source')}
                prefix={normalInputStyle ? undefined : <SearchOutlined />}
                suffix={val.trim() ? <CloseOutlined onClick={handleClose} /> : undefined}
                placeholder={placeholder}
                size={size === 'small' ? 'small' : undefined}
                onBlur={(_v, e) => {
                    thisRef.current.timer = setTimeout(() => {
                        thisRef.current.isFocus = false;
                        onBlur(e);
                    }, 200);
                }}
                onFocus={(e) => {
                    thisRef.current.isFocus = true;
                    onFocus(e);
                }}
                onKeyDown={handleKeydown}
                autoSelect={autoSelect}
                errorTrigger="blur"
            />
            {showSearchResult && showPopover && debouncedShowPopover && renderContent()}
        </div>
    );
});

export default SearchList;
